// Данные из JSON файлов бэкенда для правильной фильтрации

export interface SkinData {
  id: string;
  name: string;
  weapon: {
    id: string;
    name: string;
  };
  category: {
    id: string;
    name: string;
  };
  pattern?: {
    id: string;
    name: string;
  };
  rarity: {
    id: string;
    name: string;
    color: string;
  };
  stattrak: boolean;
  wears?: { id: string; name: string }[];
  image?: string;
}

// Глобальные переменные для хранения данных
let allSkinsData: SkinData[] = [];
let skinsDataMap = new Map<string, SkinData>();
let containerNames = new Set<string>(); // Множество имён контейнеров
let dataLoaded = false;

// Загружаем данные из JSON файлов
// NOTE: JSON файлы убраны из public/data/ — рейти приходят из API (/items → _rarity).
// Функция оставлена как no-op для обратной совместимости с импортами в MarketPage.
export async function loadSkinsData(): Promise<void> {
  dataLoaded = true;
}

// Проверяем, загружены ли данные
export function isDataLoaded(): boolean {
  return dataLoaded;
}

// Вспомогательная функция для поиска скина по имени (как get_skin_image в бэкенде)
function findSkinData(itemName: string): SkinData | undefined {
  const nameLower = itemName.toLowerCase().trim();
  
  // Очищаем имя (как в get_skin_image)
  let cleanedName = nameLower
    .replace(/stattrak™\s*/g, '')
    .replace(/souvenir\s*/g, '')
    .split('(')[0]  // Удаляем (Wear)
    .trim();
  
  // 1. Ищем по точному совпадению очищенного имени
  let skinData = skinsDataMap.get(cleanedName);
  if (skinData) return skinData;
  
  // 2. Ищем в skins_data по частичному совпадению
  for (const skin of allSkinsData) {
    const skinNameLower = skin.name.toLowerCase();
    const marketHashLower = (skin as any).market_hash_name?.toLowerCase() || '';
    
    if (cleanedName === skinNameLower || 
        cleanedName === marketHashLower ||
        skinNameLower.includes(cleanedName) ||
        cleanedName.includes(skinNameLower)) {
      return skin;
    }
  }
  
  // 3. Ищем по оригинальному имени (без очистки)
  skinData = skinsDataMap.get(nameLower);
  if (skinData) return skinData;
  
  return undefined;
}

// Функция определения редкости по данным из JSON
export function getItemRarity(itemName: string): { name: string; color: string } | null {
  const nameLower = itemName.toLowerCase().trim();
  
  // Очищаем имя для поиска (убираем StatTrak, качество и т.д.)
  let cleanedName = nameLower;
  if (cleanedName.includes('stattrak™ ')) {
    cleanedName = cleanedName.replace('stattrak™ ', '');
  }
  if (cleanedName.includes('souvenir ')) {
    cleanedName = cleanedName.replace('souvenir ', '');
  }
  // Убираем качество в скобках
  if (cleanedName.includes('(')) {
    cleanedName = cleanedName.split('(')[0].trim();
  }
  
  // Проверяем, является ли предмет контейнером
  if (containerNames.has(cleanedName) || containerNames.has(nameLower)) {
    return null;
  }
  
  // Ищем редкость в JSON данных
  // Сначала пробуем точное совпадение
  let skinData = skinsDataMap.get(cleanedName);
  if (skinData?.rarity) {
    return {
      name: skinData.rarity.name,
      color: skinData.rarity.color
    };
  }
  
  skinData = skinsDataMap.get(nameLower);
  if (skinData?.rarity) {
    return {
      name: skinData.rarity.name,
      color: skinData.rarity.color
    };
  }
  
  // Пробуем найти по частичному совпадению
  for (const [jsonName, data] of skinsDataMap) {
    if (cleanedName.includes(jsonName) || jsonName.includes(cleanedName)) {
      if (data.rarity) {
        return {
          name: data.rarity.name,
          color: data.rarity.color
        };
      }
    }
  }
  
  // Fallback - определяем по паттернам для ножей и перчаток
  if (nameLower.includes('★') || nameLower.includes('karambit') || nameLower.includes('bayonet') || 
      nameLower.includes('dagger') || nameLower.includes('hand wraps') || nameLower.includes('gloves')) {
    return { name: 'Extraordinary', color: '#eb4b4b' };
  }
  
  // Оружие
  if (nameLower.includes('howl')) return { name: 'Contraband', color: '#ffae00' };
  
  // По умолчанию
  return { name: 'Consumer Grade', color: '#b0c3d9' };
}

// Функция определения категории оружия
export function getItemCategory(itemName: string): string {
  const skinData = findSkinData(itemName);
  
  if (skinData?.category?.name) {
    return skinData.category.name;
  }
  
  // Fallback для предметов без category (crates.json, stickers.json)
  if (skinData && !skinData.category) {
    const lower = itemName.toLowerCase();
    if (lower.includes('case') || lower.includes('capsule') || lower.includes('package') || 
        lower.includes('crate') || lower.includes('souvenir')) {
      return 'Container';
    }
    if (lower.includes('sticker') && !lower.includes('capsule')) {
      return 'Sticker';
    }
    if (lower.includes('patch')) {
      return 'Patch';
    }
    if (lower.includes('graffiti')) {
      return 'Graffiti';
    }
    if (lower.includes('music kit')) {
      return 'Music Kit';
    }
  }
  if (skinData?.weapon?.name) {
    // Определяем категорию по типу оружия
    const weaponName = skinData.weapon.name.toLowerCase();
    
    if (['glock-18', 'usp-s', 'p2000', 'p250', 'cz75-auto', 'tec-9', 'five-seveN', 'desert eagle', 'r8 revolver', 'dual berettas'].some(w => weaponName.includes(w))) {
      return 'Pistols';
    }
    if (['ak-47', 'm4a4', 'm4a1-s', 'galil ar', 'famas', 'sg 553', 'aug'].some(w => weaponName.includes(w))) {
      return 'Rifles';
    }
    if (['awp', 'ssg 08', 'scar-20', 'g3sg1'].some(w => weaponName.includes(w))) {
      return 'Heavy';  // Sniper rifles grouped with Heavy
    }
    if (['mp9', 'mac-10', 'mp7', 'ump-45', 'p90', 'pp-bizon', 'mp5-sd'].some(w => weaponName.includes(w))) {
      return 'SMGs';
    }
    if (['m249', 'negev'].some(w => weaponName.includes(w))) {
      return 'Heavy';
    }
    if (['nova', 'xm1014', 'mag-7', 'sawed-off'].some(w => weaponName.includes(w))) {
      return 'Heavy';
    }
    if (['hand wraps', 'gloves'].some(w => weaponName.includes(w))) {
      return 'Gloves';
    }
  }
  
  // Fallback - определяем по полному названию
  const lower = itemName.toLowerCase();
  const weaponPart = lower.split('|')[0].trim();
  
  if (weaponPart.includes('★') || weaponPart.includes('knife') || weaponPart.includes('karambit') || 
      weaponPart.includes('bayonet') || weaponPart.includes('dagger')) {
    return 'Knives';
  }
  if (weaponPart.includes('gloves') || weaponPart.includes('hand wraps')) {
    return 'Gloves';
  }
  if (weaponPart.includes('case') || weaponPart.includes('crate') || weaponPart.includes('capsule') || 
      weaponPart.includes('package')) {
    return 'Container';
  }
  if (weaponPart.includes('sticker')) {
    return 'Sticker';
  }
  if (weaponPart.includes('patch')) {
    return 'Patch';
  }
  if (weaponPart.includes('agent') || weaponPart.includes('master agent') || weaponPart.includes('sas agent') || 
      weaponPart.includes('swat agent') || weaponPart.includes('professional agent') || weaponPart.includes('fbi agent')) {
    return 'Agent';
  }
  if (weaponPart.includes('music kit')) {
    return 'Music Kit';
  }
  if (weaponPart.includes('graffiti')) {
    return 'Graffiti';
  }
  if (weaponPart.includes('pin') || weaponPart.includes('medal') || weaponPart.includes('coin') || weaponPart.includes('badge')) {
    return 'Collectible';
  }
  
  // Определяем по ключевым словам в названии оружия
  if (['glock', 'usp', 'p2000', 'p250', 'cz75', 'tec-9', 'five-seveN', 'desert eagle', 'r8', 'dual berettas'].some(w => weaponPart.includes(w))) {
    return 'Pistols';
  }
  if (['ak-47', 'm4a4', 'm4a1', 'galil', 'famas', 'sg 553', 'aug'].some(w => weaponPart.includes(w))) {
    return 'Rifles';
  }
  if (['awp', 'ssg 08', 'scar-20', 'g3sg1'].some(w => weaponPart.includes(w))) {
    return 'Heavy';
  }
  if (['mp9', 'mac-10', 'mp7', 'ump-45', 'p90', 'pp-bizon', 'mp5'].some(w => weaponPart.includes(w))) {
    return 'SMGs';
  }
  if (['m249', 'negev'].some(w => weaponPart.includes(w))) {
    return 'Heavy';
  }
  if (['nova', 'xm1014', 'mag-7', 'sawed-off'].some(w => weaponPart.includes(w))) {
    return 'Heavy';
  }
  
  return 'Unknown';
}

// Функция определения качества (wear) из названия предмета
export function getItemWear(itemName: string): string | null {
  const lower = itemName.toLowerCase();
  
  const wears = [
    { name: 'Factory New', pattern: /\(factory new\)/i },
    { name: 'Minimal Wear', pattern: /\(minimal wear\)/i },
    { name: 'Field-Tested', pattern: /\(field-tested\)/i },
    { name: 'Well-Worn', pattern: /\(well-worn\)/i },
    { name: 'Battle-Scarred', pattern: /\(battle-scarred\)/i },
  ];
  
  for (const wear of wears) {
    if (wear.pattern.test(lower)) {
      return wear.name;
    }
  }
  
  return null;
}

// Функция проверки StatTrak
export function isStatTrak(itemName: string): boolean {
  const skinData = findSkinData(itemName);
  
  if (skinData) {
    return skinData.stattrak;
  }
  return itemName.toLowerCase().includes('stattrak');
}

// Функция получения названия оружия (без скина и качества)
export function getWeaponName(itemName: string): string {
  const skinData = findSkinData(itemName);
  
  if (skinData?.weapon?.name) {
    return skinData.weapon.name;
  }
  
  // Fallback - берём часть до |
  return itemName.split('|')[0].trim();
}

// Функция получения названия скина (pattern)
export function getSkinPattern(itemName: string): string | null {
  const skinData = findSkinData(itemName);
  
  if (skinData?.pattern?.name) {
    return skinData.pattern.name;
  }
  
  // Fallback - берём часть после | и до (
  const parts = itemName.split('|');
  if (parts.length > 1) {
    return parts[1].split('(')[0].trim();
  }
  
  return null;
}
